import { useCallback, useMemo, useRef } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import StocktakeSideMsg from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeSideMsg';
import { buildStocktakeColumns } from '#/modules/imsMgmt/receipts/stocktake/data/stocktakeColumns';
import { getStocktakeApiErrorMessage } from '#/modules/imsMgmt/receipts/stocktake/helper/apiError';
import {
  applyCreatedAtRangeToFilters,
  applyUpdatedAtRangeToFilters,
} from '#/modules/imsMgmt/receipts/stocktake/helper/stocktakeRecordDateListFilter';
import useStocktakeList from '#/modules/imsMgmt/receipts/stocktake/hooks/useStocktakeList';

const STOCKTAKE_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  stocktakeNo: '盤點單號',
};

/** 盤點列表 Tab panel（殼層提供 PageShell／標題）。 */
const StocktakeListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.STOCKTAKE_CREATE);
  const canUpdate = useCan(PERM.STOCKTAKE_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useStocktakeList();

  const setCreatedAtRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyCreatedAtRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const setUpdatedAtRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyUpdatedAtRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'createdAtFrom' && value === undefined) {
      setCreatedAtRangeFilter(undefined);
      return;
    }
    if (key === 'updatedAtFrom' && value === undefined) {
      setUpdatedAtRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setCreatedAtRangeFilter, setUpdatedAtRangeFilter]);

  const columns = useMemo(
    () => buildStocktakeColumns({
      filters: listQuery.filters || {},
      onFilterChange: handleFilterChange,
      onCreatedAtRangeChange: setCreatedAtRangeFilter,
      onUpdatedAtRangeChange: setUpdatedAtRangeFilter,
      canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
    }),
    [
      listQuery.filters,
      handleFilterChange,
      setCreatedAtRangeFilter,
      setUpdatedAtRangeFilter,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  return (
    <SideMsgLayout>
      <div className="table-panel min-h-0 xl:h-full">
        <ListTablePanel
          ref={listRef}
          tableKey="ims.stocktake.list"
          columns={columns}
          rows={rows}
          pagination={pagination}
          isLoading={isLoading}
          isError={isError}
          errorMessage={isError ? getStocktakeApiErrorMessage(error, '載入列表失敗') : undefined}
          onRetry={isError ? () => { refetch(); } : undefined}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRowClick={handleRowClick}
          onSearch={setKeyword}
          onReset={resetListQuery}
          initialKeyword={listQuery.keyword}
          searchPlaceholder="搜尋單號、備註、庫存編號或物料"
          headerAction={canCreate ? <ListAddButton to={ROUTES.stocktakeCreate} /> : null}
          listQuery={listQuery}
          filterStatusFields={filterStatusFields}
          sortFieldLabels={STOCKTAKE_SORT_FIELD_LABELS}
          onFilterChange={handleFilterChange}
          onKeywordClear={() => setKeyword('')}
          onSortRemove={removeSortAt}
          onSortingChange={handleSortingChange}
        />
      </div>

      <StocktakeSideMsg displayData={displayData} />
    </SideMsgLayout>
  );
};

export default StocktakeListPage;
